import { UserInterface } from "@swivl-mono/types";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { useInView } from "react-intersection-observer";

import { UserConversationsInterface, UsersModel } from "../../../../../Models/Users/Users.Model";
import { ClosedUserCard } from "../Components/Conversations.ClosedUserCard";
import { userPassesLocationFilter } from "../Conversations.LocationFilter";

interface ConversationsClosedUserListProps {
  conversations?: { [userId: number]: UserConversationsInterface };
  searchTerm?: string;
  selectedUserId?: number;
  allowedLocationsMap?: { [locationId: string]: boolean };
  allowedGroupIds?: Set<number>;
  hasOrgWideAccess?: boolean;
}

export function ConversationsClosedUserList(props: ConversationsClosedUserListProps) {
  const { searchTerm, selectedUserId, conversations, allowedLocationsMap, allowedGroupIds, hasOrgWideAccess } = props;
  const { loadedUsersPage, isLoadingMoreUsers, moreUsersToLoad, users, usersOnline } = UsersModel.useState((s) => s);
  const [foundUsers, setFoundUsers] = useState<UserInterface[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { ref, inView } = useInView();

  const filteredUsers = useMemo(() => {
    if (!users) {
      return [];
    }
    if (!allowedLocationsMap) {
      return [];
    }

    return Object.values(users)
      .filter((user) => {
        if (!userPassesLocationFilter(user, allowedLocationsMap ?? {}, allowedGroupIds ?? new Set(), hasOrgWideAccess ?? false)) {
          return false;
        }

        if (conversations && conversations[user.id]) {
          return false;
        }

        if ((user as any)?.deletedDate) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aDate = a.lastInteractionAt || a.lastUserMessageAt || a.updatedAt || a.createdAt;
        const bDate = b.lastInteractionAt || b.lastUserMessageAt || b.updatedAt || b.createdAt;
        const aTime = aDate ? new Date(aDate).getTime() : NaN;
        const bTime = bDate ? new Date(bDate).getTime() : NaN;
        if (Number.isFinite(aTime) && Number.isFinite(bTime)) return bTime - aTime; // Sort by last interaction date descending
        if (Number.isFinite(aTime)) return -1; // a has a valid date, b does not → a first
        if (Number.isFinite(bTime)) return 1; // b has a valid date, a does not → b first
        return 0; // Neither has a valid date, keep original order
      });
  }, [users, conversations, allowedLocationsMap, allowedGroupIds, hasOrgWideAccess, usersOnline, loadedUsersPage]);

  // search for closed users
  useEffect(() => {
    if (searchTerm) {
      const searchForUser = async () => {
        const foundUsers = await UsersModel.searchForUser(searchTerm);
        setFoundUsers(foundUsers ? foundUsers : []);
      };

      searchForUser();
    } else {
      setFoundUsers([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    UsersModel.loadUsers();
  }, []);

  useEffect(() => {
    if (!inView || !users || !moreUsersToLoad) return;

    if (containerRef.current && containerRef.current.scrollHeight <= containerRef.current.clientHeight) return; // no load if no scroll

    const prevScrollHeight = containerRef.current?.scrollHeight || 0;
    // throttle loading users
    const timeout = setTimeout(() => {
      UsersModel.loadNextPageOfArchivedUsers();

      // maintain scroll position when loading more users
      requestAnimationFrame(() => {
        const newScrollHeight = containerRef.current?.scrollHeight || 0;
        if (containerRef.current) {
          containerRef.current.scrollTop += newScrollHeight - prevScrollHeight;
        }
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [inView, users, loadedUsersPage]);

  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20, scale: 0.95 }
  };

  const filteredFoundUsers = foundUsers.filter((user) =>
    userPassesLocationFilter(user, allowedLocationsMap ?? {}, allowedGroupIds ?? new Set(), hasOrgWideAccess ?? false)
  );

  const usersToRender = searchTerm ? filteredFoundUsers : filteredUsers?.length > 0 ? filteredUsers : [];

  return (
    <motion.div
      className="flex h-full flex-col overflow-y-auto px-5 py-6 pb-[100px]"
      key="closed"
      initial="hidden"
      animate="visible"
      exit="exit"
      ref={containerRef}
      variants={fadeVariants}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <AnimatePresence mode="popLayout">
        {usersToRender.length > 0 ? (
          usersToRender.map((user) => (
            <motion.div
              key={user.id}
              initial={false}
              layout
              animate="visible"
              exit="exit"
              variants={cardVariants}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <ClosedUserCard user={user} selectedUserId={selectedUserId} isOnline={user?.id ? usersOnline?.[user?.id]?.online : false} />
            </motion.div>
          ))
        ) : (
          <div className="rounded-lg bg-grey-100 p-6 text-center">
            <p className="text-grey-600">No closed conversations</p>
          </div>
        )}
      </AnimatePresence>

      {/* triggers intersection-observer inview */}
      <div className="h-px" ref={ref} />
      {isLoadingMoreUsers && moreUsersToLoad && (
        <div className="flex justify-center">
          <CgSpinner className="mt-4 animate-spin" />
        </div>
      )}
    </motion.div>
  );
}
